import { Edit, Trash } from '@styled-icons/feather';
import { ImageElement } from '@typedream/core';
import { focusEditor, setNodes, unsetNodes } from '@udecode/plate-core';
import classNames from 'classnames';
import {
  memo, useEffect, useRef, useState,
} from 'react';
import { useTPlateEditorRef } from '../../../plate';
import { getSelectedElementEntry } from '../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../plugins/store';
import styles from '../SideToolbar.module.css';

interface AltTextInputProps {
  initialValue?: string
  onSave: (url: string) => void
}

function AltTextInput(props: AltTextInputProps) {
  const { initialValue, onSave } = props;

  const [inputURL, setInputURL] = useState(initialValue || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  return (
    <form
      className={styles.menuGroup}
      onSubmit={(e) => {
        e.preventDefault();
        onSave(inputURL);
      }}
    >
      <input
        className={classNames(styles.menuItem, styles.flex1)}
        ref={inputRef}
        type="text"
        value={inputURL}
        onChange={(e) => setInputURL(e.target.value)}
        placeholder="Describe you image here"
      />

      <button
        type="submit"
        className={classNames(styles.menuItem)}
        disabled={inputURL === initialValue}
      >
        <div className="p-1">Save</div>
      </button>
    </form>
  );
}

AltTextInput.defaultProps = {
  initialValue: '',
};

function ImageSeo() {
  const editor = useTPlateEditorRef();

  const altText = useDreamEditorStore(() => {
    const element = getSelectedElementEntry<ImageElement>(editor)?.[0];
    return element?.alt_text || '';
  });

  const [isEditing, setIsEditing] = useState(false);

  const setAltText = (value: string) => {
    const path = getSelectedElementEntry(editor)?.[1];
    setNodes(editor, { alt_text: value }, { at: path });
    setIsEditing(false);
    focusEditor(editor);
  };

  const removeAltText = () => {
    const path = getSelectedElementEntry(editor)?.[1];
    unsetNodes<ImageElement>(editor, ['alt_text'], { at: path });
    focusEditor(editor);
  };

  const renderAltText = () => {
    if (isEditing) {
      return <AltTextInput initialValue={altText} onSave={setAltText} />;
    }

    if (!altText) {
      return (
        <button
          type="button"
          className={classNames(styles.button)}
          onClick={() => setIsEditing(true)}
        >
          Add Alt Text
        </button>
      );
    }

    return (
      <div className={styles.card}>
        <div className="is-flex is-align-items-center" style={{ gap: 12 }}>
          <div
            className={classNames(
              styles.flex1,
              styles.ellipsis,
              'with-text-color',
            )}
          >
            {altText}
          </div>
          <div className="is-flex is-align-items-center">
            <button
              type="button"
              className={styles.icon}
              onClick={() => setIsEditing(true)}
            >
              <Edit size={16} />
            </button>
            <button
              type="button"
              className={styles.icon}
              onClick={removeAltText}
            >
              <Trash size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.section}>
      <span className={styles.label}>Seo</span>
      {renderAltText()}
    </div>
  );
}

export default memo(ImageSeo);
